import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../contexts/ToastContext';
import { setAllPending, setPending, requestSync } from '../redux/slices/djLibraryChangesSlice';
import { setRekordboxSidebarOpen } from '../redux/slices/rekordboxSlice';
import { setSeratoSidebarOpen } from '../redux/slices/seratoSlice';
import { setTraktorSidebarOpen } from '../redux/slices/traktorSlice';
import { setVirtualDJSidebarOpen } from '../redux/slices/virtualdjSlice';
import { track } from '../utils/analytics';
import rekordboxLogo from '../assets/rekordbox-logo.png';
import seratoLogo from '../assets/serato-logo-10px.png';
import traktorLogo from '../assets/traktor-logo.png';
import virtualdjLogo from '../assets/virtual-dj-logo.png';

const LOGO = { rekordbox: rekordboxLogo, serato: seratoLogo, traktor: traktorLogo, virtualdj: virtualdjLogo };
const OPEN = { rekordbox: setRekordboxSidebarOpen, serato: setSeratoSidebarOpen, traktor: setTraktorSidebarOpen, virtualdj: setVirtualDJSidebarOpen };

/**
 * Bridges the main-process DJ-library watcher into the UI. Renders nothing.
 * - keeps redux `djLibraryChanges.pending` current (rail dot + sidebar banner)
 * - shows one in-app card per fresh change with a "Sync now" action
 * - handles the OS-notification click (`djlibrary:open`): opens that app's
 *   sidebar and asks it to sync
 */
function stickyForDebug() {
    try { return localStorage.getItem('djChangeToastSticky') === '1'; } catch { return false; }
}

export default function DjLibraryChangeListener() {
    const dispatch = useDispatch();
    const toast = useToast();
    const toastRef = useRef(toast);
    toastRef.current = toast;
    const cardIds = useRef({}); // app -> sticky card id, so a sync from elsewhere can dismiss it
    // Auto-sync prefs (AutoSyncManager acts on the same pending state): when
    // it will import a change itself, the "Sync now" card is noise — its
    // receipt toast replaces it. Ref, so the IPC subscription needn't rebind.
    const autoSyncPrefs = useSelector((s) => s.djAutoSync.prefs);
    const autoSyncPrefsRef = useRef(autoSyncPrefs);
    autoSyncPrefsRef.current = autoSyncPrefs;
    // Account switch: getPending() filters by the OWNER's sync relationships,
    // so refetching on userId change both drops the previous account's dots
    // and loads the new account's persisted ones.
    const userId = useSelector((s) => s.auth?.user?.id) || null;

    useEffect(() => {
        const api = window.electron?.djlibrary;
        if (!api) return undefined;

        const syncNow = (app, from) => {
            track('dj_library_change_sync_clicked', { app, from });
            dispatch(requestSync(app));
            dispatch(OPEN[app](true));
        };

        api.getPending().then((p) => { dispatch(setAllPending(p || {})); }).catch(() => {});

        const offChanged = api.onChanged((payload) => {
            if (!payload) return;
            const { app, diff, label, summary, at, fresh } = payload;
            if (!diff) {
                dispatch(setPending({ app, change: null }));
                if (cardIds.current[app]) { toastRef.current.dismiss?.(cardIds.current[app]); delete cardIds.current[app]; }
                return;
            }
            dispatch(setPending({ app, change: { label, summary, diff, at } }));
            if (fresh) {
                const prefs = autoSyncPrefsRef.current?.[app];
                const hasNew = diff.newPlaylists.length > 0;
                const autoHandled = !!prefs?.enabled && (!hasNew || prefs.includeNew);
                // Auto-sync ON but the diff brings never-imported playlists →
                // still needs the user (the picker opens via syncNow); frame
                // the card as a review, not a chore.
                const needsReview = !!prefs?.enabled && hasNew && !prefs.includeNew;
                track('dj_library_change_detected', {
                    app,
                    new_playlists: diff.newPlaylists.length,
                    renamed: diff.renamed.length,
                    tracks_added: diff.tracksAdded.reduce((n, t) => n + t.count, 0),
                    tracks_removed: diff.tracksRemoved.reduce((n, t) => n + t.count, 0),
                    removed_playlists: diff.removedPlaylists.length,
                    auto_sync: autoHandled ? 'auto' : (needsReview ? 'review' : 'off'),
                });
                if (autoHandled) return; // AutoSyncManager imports it; its receipt replaces this card
                cardIds.current[app] = toastRef.current.card({
                    artwork: LOGO[app],
                    artworkKind: 'logo',
                    title: needsReview ? `New in ${label}` : `Updates from ${label}`,
                    message: summary,
                    action: { label: needsReview ? 'Review' : 'Sync now', onClick: () => syncNow(app, 'toast') },
                    // 20s — long enough to read and click; the rail dot stays as
                    // the persistent signal. Dev aid: `localStorage.djChangeToastSticky = '1'`
                    // keeps it up forever (styling work). Remove the key to restore.
                    duration: stickyForDebug() ? 0 : 20000,
                });
            }
        });
        const offOpen = api.onOpen(({ app } = {}) => { if (app && OPEN[app]) syncNow(app, 'os_notification'); });
        return () => { offChanged?.(); offOpen?.(); };
    }, [dispatch, userId]);

    return null;
}
